import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader } from "@/crm/ui";
import { toast } from "sonner";
import { UserPlus, Trash2, X, Pencil, LogIn, Search, AlertTriangle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/CrmAuth";
import BaseModal from "@/crm/components/Modal";

const ROLE_BADGE = { admin: "bg-rose-100 text-rose-700", manager: "bg-indigo-100 text-indigo-700", commercial: "bg-emerald-100 text-emerald-700" };
const ROLE_LABEL = { admin: "Administrateur", manager: "Manager", commercial: "Commercial" };
const ROLE_ORDER = ["admin", "manager", "commercial"];
const AVATAR_BG = ["bg-indigo-500", "bg-sky-600", "bg-emerald-600", "bg-amber-600", "bg-fuchsia-600", "bg-teal-600"];

const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const FIELD = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15";
const ICON_BTN = "p-2 rounded-lg text-slate-400 transition-colors";

export default function Team() {
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Supprimé"); setDeleting(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !(u.active !== false) }); load(); };
  const switchTo = async (u) => {
    try { await impersonate(u.id); toast.success(`Connecté en tant que ${u.name}`); navigate("/"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const counts = useMemo(() => {
    const list = users || [];
    return {
      total: list.length,
      admin: list.filter((u) => u.role === "admin").length,
      manager: list.filter((u) => u.role === "manager").length,
      commercial: list.filter((u) => u.role === "commercial").length,
    };
  }, [users]);

  const rows = useMemo(() => {
    const q = norm(query.trim());
    return (users || []).filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return norm(`${u.name} ${u.email}`).includes(q);
    });
  }, [users, query, roleFilter]);

  if (!users) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Équipe commerciale"
        subtitle="Rôles, secteurs attribués et accès des membres de l'espace."
        actions={
          <button data-testid="new-user-btn" onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">
            <UserPlus size={16} /> Ajouter un membre
          </button>
        }
      />

      {/* Compteurs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4" data-testid="team-counters">
        {[
          { label: "Membres", value: counts.total, dot: "bg-indigo-600" },
          { label: "Administrateurs", value: counts.admin, dot: "bg-rose-500" },
          { label: "Managers", value: counts.manager, dot: "bg-indigo-400" },
          { label: "Commerciaux", value: counts.commercial, dot: "bg-emerald-500" },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className={`h-2 w-2 rounded-full ${c.dot}`} /> {c.label}
            </div>
            <div className="mt-1.5 text-2xl font-mono">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Recherche + filtre de rôle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <Search size={15} className="text-slate-400 shrink-0" />
          <input data-testid="team-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un nom ou un email…"
            className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 overflow-x-auto">
          {[{ id: "all", label: "Tous" }, ...ROLE_ORDER.map((r) => ({ id: r, label: ROLE_LABEL[r] }))].map((t) => (
            <button key={t.id} data-testid={`team-filter-${t.id}`} onClick={() => setRoleFilter(t.id)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${roleFilter === t.id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tableau (desktop) / cartes (mobile) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hidden lg:block">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase">
            <tr>
              <th className="p-3 text-left w-[30%]">Membre</th>
              <th className="p-3 text-left w-[15%]">Rôle</th>
              <th className="p-3 text-left">Secteurs (NAF)</th>
              <th className="p-3 text-left w-[10%]">Statut</th>
              <th className="p-3 text-right w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((u, i) => (
              <tr key={u.id} className="hover:bg-slate-50/70" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-9 w-9 shrink-0 rounded-full ${AVATAR_BG[i % AVATAR_BG.length]} text-white flex items-center justify-center text-xs font-semibold`}>{u.name[0]}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 truncate">
                        <span className="truncate">{u.name}</span>
                        {u.id === me?.id && <span className="text-[10px] font-mono text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">vous</span>}
                      </div>
                      <div className="text-xs font-mono text-slate-400 truncate">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-md ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
                <td className="p-3"><Sectors user={u} /><CopilotBadge user={u} /></td>
                <td className="p-3"><StatusButton user={u} onToggle={toggleActive} /></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-0.5">
                    <RowActions u={u} me={me} onImpersonate={switchTo} onEdit={setEditing} onDelete={setDeleting} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <EmptyState onReset={() => { setQuery(""); setRoleFilter("all"); }} />}
      </div>

      <div className="flex flex-col gap-2 lg:hidden">
        {rows.map((u, i) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-9 w-9 shrink-0 rounded-full ${AVATAR_BG[i % AVATAR_BG.length]} text-white flex items-center justify-center text-xs font-semibold`}>{u.name[0]}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 truncate">
                  <span className="truncate">{u.name}</span>
                  {u.id === me?.id && <span className="text-[10px] font-mono text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">vous</span>}
                </div>
                <div className="text-xs font-mono text-slate-400 truncate">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mt-3">
              <span className={`text-xs px-2 py-0.5 rounded-md ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span>
              <StatusButton user={u} onToggle={toggleActive} />
            </div>
            <div className="mt-3"><Sectors user={u} /><CopilotBadge user={u} /></div>
            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
              <RowActions u={u} me={me} onImpersonate={switchTo} onEdit={setEditing} onDelete={setDeleting} />
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200">
            <EmptyState onReset={() => { setQuery(""); setRoleFilter("all"); }} />
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">{rows.length} membre(s) affiché(s) sur {users.length}</p>

      {showNew && <NewUser onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <EditUser user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <DeleteUser user={deleting} onClose={() => setDeleting(null)} onConfirm={() => del(deleting)} />}
    </div>
  );
}

function Sectors({ user }) {
  const list = user.sector_naf_prefixes || [];
  if (!list.length) return <span className="text-xs text-slate-300">Tous les secteurs</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((s) => <span key={s} className="text-xs bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5 font-mono">{s}</span>)}
    </div>
  );
}

/** Visible seulement quand l'admin a retiré le copilote automatique : le cas
 *  normal (autorisé) n'a pas besoin d'être dit sur chaque ligne. */
function CopilotBadge({ user }) {
  if (user.copilot_auto_suggest_allowed !== false) return null;
  return (
    <span data-testid={`copilot-locked-${user.id}`}
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
      <Sparkles size={11} /> Copilote auto désactivé
    </span>
  );
}

/** Réglage admin : suggestions automatiques du copilote d'appel pour ce membre.
 *  Le commercial garde le bouton « Suggérer » ; c'est l'automatisme qu'on retire. */
function CopilotToggle({ value, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 border border-slate-200 rounded-xl p-4 bg-slate-50/60 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-medium"><Sparkles size={14} className="text-indigo-500" /> Suggestions automatiques du copilote</span>
        <span className="block text-xs text-slate-400 mt-1">
          {value
            ? "Pendant ses appels, l'IA suit la conversation et propose d'elle-même la suite (playbook + connaissance de l'entreprise). Le commercial peut la couper depuis le panneau d'appel."
            : "Désactivées pour ce membre : l'interrupteur du panneau d'appel est verrouillé. Le bouton « Suggérer » reste disponible."}
        </span>
      </span>
    </label>
  );
}

function StatusButton({ user, onToggle }) {
  const active = user.active !== false;
  return (
    <button onClick={() => onToggle(user)} title={active ? "Désactiver l'accès" : "Réactiver l'accès"}
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
      {active ? "Actif" : "Inactif"}
    </button>
  );
}

function RowActions({ u, me, onImpersonate, onEdit, onDelete }) {
  return (
    <>
      {u.role !== "admin" && u.id !== me?.id && u.active !== false && (
        <button data-testid={`impersonate-${u.id}`} onClick={() => onImpersonate(u)} title={`Se connecter en tant que ${u.name}`}
          className={`${ICON_BTN} hover:bg-amber-50 hover:text-amber-600`}><LogIn size={16} /></button>
      )}
      <button data-testid={`edit-user-${u.id}`} onClick={() => onEdit(u)} title="Modifier"
        className={`${ICON_BTN} hover:bg-indigo-50 hover:text-indigo-600`}><Pencil size={16} /></button>
      <button data-testid={`delete-user-${u.id}`} onClick={() => onDelete(u)} title="Supprimer"
        className={`${ICON_BTN} hover:bg-rose-50 hover:text-rose-600`}><Trash2 size={16} /></button>
    </>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="py-12 text-center" data-testid="team-empty">
      <p className="text-sm text-slate-500">Aucun membre ne correspond à cette recherche.</p>
      <button onClick={onReset} className="mt-3 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">Réinitialiser les filtres</button>
    </div>
  );
}

// `width` conservé (classe `max-w-*`) pour ne pas toucher aux appels de la
// page ; tailwind-merge la fait gagner sur la largeur par défaut.
function Modal({ children, onClose, testid, width = "max-w-md" }) {
  return (
    <BaseModal onClose={onClose} testid={testid} className={width}>
      {children}
    </BaseModal>
  );
}

function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="min-w-0">
        <h3 className="font-heading font-bold text-lg">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <button onClick={onClose} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X size={18} /></button>
    </div>
  );
}

function ModalFooter({ onClose, onSave, saveLabel, testid, danger }) {
  return (
    <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
      <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">Annuler</button>
      <button data-testid={testid} onClick={onSave}
        className={`px-4 py-2 rounded-lg text-white text-sm ${danger ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"}`}>{saveLabel}</button>
    </div>
  );
}

function RoleSelect({ value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">Rôle</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${FIELD} mt-1 bg-white`}>
        <option value="commercial">Commercial</option>
        <option value="manager">Manager</option>
        <option value="admin">Administrateur</option>
      </select>
      <span className="block text-xs text-slate-400 mt-1.5">
        {value === "admin" && "Accès complet, facturation et gestion de l'équipe."}
        {value === "manager" && "Pilote le pipeline et suit les commerciaux."}
        {value === "commercial" && "Prospecte sur les secteurs NAF attribués."}
      </span>
    </label>
  );
}

function SectorPicker({ value, onChange }) {
  const [sectors, setSectors] = useState([]);
  const [manual, setManual] = useState("");
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/sectors").then(({ data }) => setSectors(data)).catch(() => {}); }, []);
  const known = new Set(sectors.map((s) => s.code));
  const toggle = (code) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  const addManual = () => {
    const code = manual.trim().slice(0, 2);
    if (!/^\d{2}$/.test(code)) return toast.error("Préfixe NAF invalide (2 chiffres)");
    if (!value.includes(code)) onChange([...value, code]);
    setManual("");
  };
  const q = norm(filter.trim());
  const visible = sectors.filter((s) => !q || norm(`${s.code} ${s.label}`).includes(q));
  const extras = value.filter((c) => !known.has(c));

  return (
    <div data-testid="sector-picker" className="border border-slate-200 rounded-xl p-4 bg-slate-50/60">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">Secteurs NAF autorisés</span>
        <span className="text-xs font-mono text-slate-400">{value.length} sélectionné(s)</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">Sans sélection, le commercial voit toutes les entreprises de l'espace.</p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {value.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full pl-2.5 pr-1.5 py-1">
              <span className="font-mono">{c}</span>
              <button onClick={() => toggle(c)} className="text-indigo-400 hover:text-indigo-700"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 mt-3">
        <Search size={14} className="text-slate-400 shrink-0" />
        <input data-testid="sector-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrer par code ou libellé…" className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
      </div>

      <div className="mt-2 max-h-44 overflow-y-auto space-y-0.5">
        {visible.map((s) => (
          <label key={s.code} className="flex items-center gap-2.5 text-sm cursor-pointer hover:bg-white rounded-lg px-2 py-1.5">
            <input type="checkbox" data-testid={`sector-check-${s.code}`} checked={value.includes(s.code)} onChange={() => toggle(s.code)} />
            <span className="font-mono text-xs text-indigo-600 shrink-0">{s.code}</span>
            <span className="truncate">{s.label}</span>
            <span className="ml-auto text-xs text-slate-400 shrink-0">{s.count}</span>
          </label>
        ))}
        {extras.map((c) => (
          <label key={c} className="flex items-center gap-2.5 text-sm cursor-pointer px-2 py-1.5">
            <input type="checkbox" checked onChange={() => toggle(c)} />
            <span className="font-mono text-xs text-indigo-600">{c}</span>
            <span className="text-xs text-slate-400">(manuel)</span>
          </label>
        ))}
        {sectors.length === 0 && <p className="text-xs text-slate-400 px-2 py-2">Aucun secteur détecté dans la base — ajoutez un préfixe manuellement.</p>}
        {sectors.length > 0 && visible.length === 0 && <p className="text-xs text-slate-400 px-2 py-3 text-center">Aucun secteur ne correspond au filtre.</p>}
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
        <input data-testid="sector-manual" placeholder="Préfixe (ex: 56)" value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
          className="w-32 px-2 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" />
        <button data-testid="sector-manual-add" onClick={addManual} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm bg-white hover:bg-slate-50">Ajouter</button>
      </div>
    </div>
  );
}

function NewUser({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "commercial" });
  const [sectors, setSectors] = useState([]);
  const [copilotAuto, setCopilotAuto] = useState(true);
  const save = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("Tous les champs sont requis");
    try {
      await api.post("/users", { ...f, sector_naf_prefixes: f.role === "commercial" ? sectors : [],
                                 copilot_auto_suggest_allowed: copilotAuto });
      toast.success("Membre ajouté"); onSaved();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} testid="new-user-modal">
      <ModalHeader title="Nouveau membre" subtitle="Le membre pourra se connecter avec l'email et le mot de passe définis ici." onClose={onClose} />
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-slate-500">Nom complet</span>
            <input data-testid="user-name" placeholder="Camille Roux" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${FIELD} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Email</span>
            <input data-testid="user-email" placeholder="camille@exemple.fr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${FIELD} mt-1 font-mono`} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-slate-500">Mot de passe</span>
          <input data-testid="user-password" placeholder="8 caractères minimum" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={`${FIELD} mt-1`} />
        </label>
        <RoleSelect testid="user-role" value={f.role} onChange={(role) => setF({ ...f, role })} />
        {f.role === "commercial" && <SectorPicker value={sectors} onChange={setSectors} />}
        <CopilotToggle testid="user-copilot-auto" value={copilotAuto} onChange={setCopilotAuto} />
      </div>
      <ModalFooter onClose={onClose} onSave={save} saveLabel="Ajouter le membre" testid="save-user" />
    </Modal>
  );
}

function EditUser({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user.role);
  const [sectors, setSectors] = useState(user.sector_naf_prefixes || []);
  const [copilotAuto, setCopilotAuto] = useState(user.copilot_auto_suggest_allowed !== false);
  const save = async () => {
    if (!name.trim()) return toast.error("Le nom est obligatoire");
    if (!email.trim()) return toast.error("L'email est obligatoire");
    if (password && password.length < 6) return toast.error("Mot de passe : 6 caractères minimum");
    // Identité et mot de passe ne partent que s'ils changent : un PUT sans
    // changement ne doit ni réécrire l'email ni toucher au mot de passe.
    const body = { role, sector_naf_prefixes: role === "commercial" ? sectors : [],
                   copilot_auto_suggest_allowed: copilotAuto };
    if (name.trim() !== user.name) body.name = name.trim();
    if (email.trim().toLowerCase() !== (user.email || "").toLowerCase()) body.email = email.trim().toLowerCase();
    if (password) body.password = password;
    try {
      await api.put(`/users/${user.id}`, body);
      toast.success("Membre mis à jour"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} testid="edit-user-modal">
      <ModalHeader title={`Modifier ${user.name}`} subtitle={user.email} onClose={onClose} />
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-slate-500">Nom complet</span>
            <input data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)} className={`${FIELD} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Email de connexion</span>
            <input data-testid="edit-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${FIELD} mt-1 font-mono`} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-slate-500">Nouveau mot de passe</span>
          <input data-testid="edit-user-password" type="password" autoComplete="new-password" placeholder="Laisser vide pour ne pas le changer"
            value={password} onChange={(e) => setPassword(e.target.value)} className={`${FIELD} mt-1`} />
        </label>
        <RoleSelect testid="edit-user-role" value={role} onChange={setRole} />
        {role === "commercial" && <SectorPicker value={sectors} onChange={setSectors} />}
        <CopilotToggle testid="edit-user-copilot-auto" value={copilotAuto} onChange={setCopilotAuto} />
      </div>
      <ModalFooter onClose={onClose} onSave={save} saveLabel="Enregistrer" testid="save-edit-user" />
    </Modal>
  );
}

function DeleteUser({ user, onClose, onConfirm }) {
  return (
    <Modal onClose={onClose} testid="delete-user-modal" width="max-w-sm">
      <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><AlertTriangle size={19} /></div>
      <h3 className="font-heading font-bold text-lg">Supprimer ce membre ?</h3>
      <p className="text-sm text-slate-500 mt-2">
        {user.name} ({user.email}) perdra immédiatement l'accès à l'espace. Ses entreprises attribuées seront à réaffecter.
      </p>
      <ModalFooter onClose={onClose} onSave={onConfirm} saveLabel="Supprimer" testid="delete-confirm" danger />
    </Modal>
  );
}
